// Extracted from web/App.tsx; behavior and declaration bodies preserved.
import { useState } from "react";
import { Plus } from "lucide-react";
import type { DomainObject, ModelDocument, Operation, Preferences } from "../../shared/contracts";

import { vocabulary } from "../../shared/entityPresentation";
import { createObject, defaultEntityFields, uid } from "../../core";

import { Button, Field, Modal } from "../components/Primitives";

export function ObjectDialog({
  document,
  preferences,
  diagramId,
  ownerId,
  position,
  onClose,
  onCreate,
}: {
  document: ModelDocument;
  preferences: Preferences;
  diagramId?: string;
  ownerId?: string;
  position?: {x:number;y:number};
  onClose: () => void;
  onCreate: (ops: Operation[], id: string) => void;
}) {
  const [name, setName] = useState("");
  const [type, setType] = useState("");
  const [nullable, setNullable] = useState(true);
  const [description, setDescription] = useState("");
  const owner = document.objects.find((o) => o.objectId === ownerId);
  const diagram = document.diagrams.find((d) => d.diagramId === diagramId);
  const repr = document.representations.find(
    (r) =>
      r.representationId ===
      (owner?.representationId ||
        diagram?.representationId ||
        document.model.activeRepresentation),
  );
  const words = vocabulary(document, repr?.representationId, owner, diagram?.perspective);
  const physical = words.physical;
  const kind: DomainObject["kind"] = ownerId
    ? document.model.family === "document"
      ? "property"
      : document.model.family === "semantic"
        ? "semanticField"
        : physical
          ? "column"
          : "attribute"
    : document.model.family === "document"
      ? "collection"
      : document.model.family === "semantic"
        ? "semanticEntity"
        : document.model.family === "analytical"
          ? "fact"
          : physical
            ? "table"
            : repr?.kind === "conceptual"
              ? "concept"
              : "entity";
  const create = () => {
    const object = {
      ...createObject(
        kind,
        name.trim(),
        repr?.representationId || document.model.activeRepresentation,
        ownerId,
      ),
      description,
      ...(ownerId
        ? {
            ...(physical ? {type:{name:type || "VARCHAR"}} : words.conceptual ? {} : {logicalType:type || "string"}),
            nullable,
            ordinal: document.objects.filter((o) => o.ownerObjectId === ownerId)
              .length,
          }
        : {}),
    };
    const ops: Operation[] = [{ type: "object.create", object }, ...(!ownerId ? defaultEntityFields(document, object, preferences) : [])];
    if (!ownerId && diagram) {
      const count = diagram.appearances.length;
      ops.push({
        type: "appearance.create",
        diagramId: diagram.diagramId,
        appearance: {
          appearanceId: uid(),
          objectId: object.objectId,
          position: position || {
            x: 80 + (count % 3) * 330,
            y: 80 + Math.floor(count / 3) * 280,
          },
          width: 260,
          sizeLocked: false,
        },
      });
    }
    onCreate(ops, object.objectId);
  };
  return (
    <Modal
      title={
        ownerId
          ? `Add ${physical ? "a column" : "an attribute"} to ${owner?.name}`
          : `Create ${kind === "table" ? "a table" : kind === "collection" ? "a collection" : "an entity"}`
      }
      subtitle={
        ownerId
          ? "Describe a single property of this object."
          : "Start with a name and meaning. Define fields and keys as you go."
      }
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button
            icon={Plus}
            variant="primary"
            disabled={!name.trim()}
            onClick={create}
          >
            Add to draft
          </Button>
        </>
      }
    >
      <Field label={ownerId ? "Field name" : "Name"}>
        <input
          autoFocus
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && name.trim()) create();
          }}
          placeholder={ownerId ? "e.g. customer_id" : "e.g. Customer"}
        />
      </Field>
      <Field label="Description">
        <textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          rows={3}
          placeholder="What does this mean to the business?"
        />
      </Field>
      {ownerId && (
        <>
          {!words.conceptual && <Field label={physical ? "Data type" : "Logical data type"}>
            <input
              list="new-field-types"
              value={type || (physical ? "VARCHAR" : "string")}
              onChange={(e) => setType(e.target.value)}
            />
            <datalist id="new-field-types">
              {(physical ? [
                "varchar",
                "text",
                "integer",
                "bigint",
                "uuid",
                "boolean",
                "date",
                "timestamp",
                "decimal",
                "jsonb",
              ] : ["string", "identifier", "integer", "decimal", "date", "boolean"]).map((t) => (
                <option key={t}>{t}</option>
              ))}
            </datalist>
          </Field>}
          <label className="check-row">
            <input
              type="checkbox"
              checked={nullable}
              onChange={(e) => setNullable(e.target.checked)}
            />
            Allow null values
          </label>
        </>
      )}
    </Modal>
  );
}
